import { type InputHTMLAttributes, forwardRef } from 'react';
import { formStyles } from '@utils/formStyles';

interface FormSearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    label?: string;
    error?: string;
    helpText?: string;
    fullWidth?: boolean;
    /** Drops the default bottom margin, for inline use in toolbars. */
    noMargin?: boolean;
    showSearchIcon?: boolean;
}

const FormSearchInput = forwardRef<HTMLInputElement, FormSearchInputProps>(
    ({ label, error, helpText, fullWidth = true, noMargin = false, showSearchIcon = true, className = '', ...rest }, ref) => {
        const inputClasses = `${error ? formStyles.inputError : formStyles.input} ${formStyles.placeholder} ${className}`;

        let containerClasses;

        if (noMargin) {
            containerClasses = fullWidth ? formStyles.fullWidthContainerNoMargin : formStyles.containerNoMargin;
        } else {
            containerClasses = fullWidth ? formStyles.fullWidthContainer : formStyles.container;
        }

        return (
            <div className={containerClasses}>
                {label && (
                    <label className={formStyles.label} htmlFor={rest.id}>
                        {label}
                    </label>
                )}
                <div className="relative">
                    {showSearchIcon && (
                        <svg
                            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                            />
                        </svg>
                    )}
                    <input
                        className={`${inputClasses} ${showSearchIcon ? 'pl-10' : ''}`}
                        placeholder={rest.placeholder || 'Search...'}
                        ref={ref}
                        type="search"
                        {...rest}
                    />
                </div>
                {helpText && <div className={formStyles.helpText}>{helpText}</div>}
                {error && <div className={formStyles.errorText}>{error}</div>}
            </div>
        );
    }
);

FormSearchInput.displayName = 'FormSearchInput';

export default FormSearchInput;
